import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { REFERRAL_URGENCY_LABELS, type ReferralUrgency } from '@wodi/domain';
import type { ReferralLetterView, ReferralPreviewView } from '@wodi/shared';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  LoadingBlock,
  Notice,
  RadioGroup,
  TextArea,
  TextField,
} from '@wodi/ui';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';

import { ApiError } from '../api/client.ts';
import { issueReferral, referralPreviewQuery } from '../api/clinical-documents.ts';
import { referralKeys } from '../api/referrals.ts';
import { can, sessionQuery } from '../api/session.ts';
import { formatInstant } from '../patients/format.ts';
import {
  FacilityPicker,
  facilityFields,
  type FacilityChoice,
} from '../referrals/facility-picker.tsx';
import { toasts } from '../toasts.ts';
import styles from './documents.module.css';
import { ReferralPrinter } from './prints.tsx';

// Writing a referral letter (docs/plans/phase1-consultation.md §7.4; S4, TODO(verify-spec)):
// the receiving facility from the Facility Registry cache, or typed with an optional MFL code
// when it isn't there (docs/plans/phase2-referrals.md §1.1 item 2), the service, urgency and
// reason, and a summary the server snapshots from the visit at issue. Issuing opens the
// referral record that tracks it. The clinician
// chooses which released results go in and writes the summary paragraph; the rest is
// shown as it will be printed. After issuing, the A4 preview opens for printing.

function allergyWords(preview: ReferralPreviewView): string {
  if (preview.allergies.state === 'allergies') return preview.allergies.items.join(', ');
  return preview.allergies.state === 'no_known_allergies'
    ? 'No known allergies'
    : 'Allergies not recorded';
}

export function SummaryPreview({
  preview,
  chosen,
  onChosenChange,
}: {
  preview: ReferralPreviewView;
  chosen: ReadonlySet<string>;
  onChosenChange: (next: Set<string>) => void;
}) {
  return (
    <div className={styles.summary} aria-label="What the letter carries" role="group">
      <p className={styles.summaryLine}>
        {`Diagnoses: ${
          preview.diagnoses.length === 0
            ? 'None recorded'
            : preview.diagnoses.map((d) => d.display).join('; ')
        }`}
      </p>
      <p className={styles.summaryLine}>{`Allergies: ${allergyWords(preview)}`}</p>
      <p className={styles.summaryLine}>
        {`Latest vitals: ${
          preview.vitals.length === 0
            ? 'Not taken'
            : preview.vitals.map((v) => `${v.label} ${v.value}`).join(', ')
        }`}
      </p>
      <p className={styles.summaryLine}>
        {`Current medicines: ${
          preview.medicines.length === 0
            ? 'None prescribed in this visit'
            : preview.medicines.map((m) => m.display).join('; ')
        }`}
      </p>
      {preview.results.length === 0 ? (
        <p className={styles.summaryLine}>Results: none released in this visit</p>
      ) : (
        <>
          <p className={styles.summaryLine}>Results to include</p>
          {preview.results.map((r) => (
            <Checkbox
              key={r.id}
              isSelected={chosen.has(r.id)}
              onChange={(on) => {
                const next = new Set(chosen);
                if (on) next.add(r.id);
                else next.delete(r.id);
                onChosenChange(next);
              }}
              description={`Released ${formatInstant(r.releasedAt)}`}
            >
              {r.text}
            </Checkbox>
          ))}
        </>
      )}
    </div>
  );
}

function ReferralForm({
  visitId,
  consultationId,
  listKeys,
  onIssued,
  onCancel,
}: {
  visitId: string;
  consultationId: string | null;
  listKeys: readonly QueryKey[];
  onIssued: (letter: ReferralLetterView) => void;
  onCancel: () => void;
}) {
  const queryClient = useQueryClient();
  const preview = useQuery(referralPreviewQuery(visitId));
  const { data: session } = useQuery(sessionQuery);
  const [clientRequestId] = useState(() => crypto.randomUUID());
  const [facility, setFacility] = useState<FacilityChoice | null>(null);
  const [service, setService] = useState('');
  const [urgency, setUrgency] = useState<ReferralUrgency>('routine');
  const [reason, setReason] = useState('');
  const [summaryText, setSummaryText] = useState('');
  const [chosen, setChosen] = useState<Set<string> | null>(null);
  const [tried, setTried] = useState(false);
  // Every released result is included until the clinician unticks it.
  const results = chosen ?? new Set(preview.data?.results.map((r) => r.id) ?? []);

  const to = facilityFields(facility);
  const facilityError =
    to.name === '' ? 'Choose the facility you are referring to, or type its name.' : null;
  const reasonError = reason.trim() === '' ? 'Enter the reason for the referral.' : null;

  const issue = useMutation({
    mutationFn: () =>
      issueReferral(visitId, {
        clientRequestId,
        consultationId,
        registryFacilityId: to.registryFacilityId,
        toFacilityName: to.name,
        toFacilityMflCode: to.mflCode,
        toService: service.trim() === '' ? null : service.trim(),
        urgency,
        reason: reason.trim(),
        resultIds: [...results],
        clinicianText: summaryText,
      }),
    onSuccess: (letter) => {
      toasts.show({ title: 'Referral sent' });
      for (const key of listKeys) void queryClient.invalidateQueries({ queryKey: key });
      void queryClient.invalidateQueries({ queryKey: referralKeys.all });
      onIssued(letter);
    },
  });
  const serverError = issue.error instanceof ApiError ? issue.error.message : null;

  return (
    <>
      <div className={styles.fields}>
        <FacilityPicker
          label="To facility"
          value={facility}
          onChange={setFacility}
          mayRegistrySearch={
            session !== null && session !== undefined && can(session, 'registries.search')
          }
          errorMessage={tried && facilityError !== null ? facilityError : undefined}
        />
        <TextField
          label="Department or clinician (optional)"
          value={service}
          onChange={setService}
        />
        <RadioGroup
          label="Urgency"
          value={urgency}
          onChange={(v) => {
            setUrgency(v as ReferralUrgency);
          }}
          options={(['routine', 'urgent', 'emergency'] as const).map((id) => ({
            id,
            label: REFERRAL_URGENCY_LABELS[id],
          }))}
        />
        <TextArea
          label="Reason for referral"
          value={reason}
          onChange={setReason}
          rows={3}
          isRequired
          {...(tried && reasonError !== null ? { errorMessage: reasonError } : {})}
        />
        {preview.isPending ? (
          <LoadingBlock label="Loading what the letter will carry" />
        ) : preview.isError ? (
          <Notice
            tone="error"
            action={<Button onPress={() => void preview.refetch()}>Try again</Button>}
          >
            {preview.error instanceof ApiError
              ? preview.error.message
              : 'The summary could not load. Try again.'}
          </Notice>
        ) : (
          <SummaryPreview preview={preview.data} chosen={results} onChosenChange={setChosen} />
        )}
        <TextArea
          label="Summary for the receiving clinician (optional)"
          value={summaryText}
          onChange={setSummaryText}
          rows={4}
        />
        {serverError === null ? null : (
          <p className={styles.help} role="alert">
            {serverError}
          </p>
        )}
      </div>
      <DialogActions>
        <Button variant="quiet" onPress={onCancel}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isPending={issue.isPending}
          pendingLabel="Issuing…"
          onPress={() => {
            setTried(true);
            if (facilityError !== null || reasonError !== null || !preview.isSuccess) return;
            issue.mutate();
          }}
        >
          Issue referral letter
        </Button>
      </DialogActions>
    </>
  );
}

/** "Write referral letter" and its dialog: the form, then the print preview. */
export function WriteReferral(props: {
  visitId: string;
  consultationId: string | null;
  listKeys: readonly QueryKey[];
  isDisabled: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [issued, setIssued] = useState<ReferralLetterView | null>(null);
  const close = () => {
    setOpen(false);
    setIssued(null);
  };
  return (
    <>
      <Button
        isDisabled={props.isDisabled}
        onPress={() => {
          setOpen(true);
        }}
      >
        Write referral letter
      </Button>
      {open ? (
        <Dialog
          title={issued === null ? 'Referral letter' : 'Print referral letter'}
          isOpen
          onOpenChange={(next) => {
            if (!next) close();
          }}
        >
          {issued === null ? (
            <ReferralForm {...props} onIssued={setIssued} onCancel={close} />
          ) : (
            <>
              <ReferralPrinter id={issued.id} listKeys={props.listKeys} />
              {issued.referralId === null ? null : (
                <p className={styles.help}>
                  {'Record what the receiving facility says on the '}
                  <Link to="/referrals/$referralId" params={{ referralId: issued.referralId }}>
                    referral page
                  </Link>
                  .
                </p>
              )}
              <DialogActions>
                <Button onPress={close}>Done</Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      ) : null}
    </>
  );
}
